import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star, Plus, Send, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

export default function Feedback() {
  const [list, setList] = useState([]);
  const pg = usePagination(list);
  const [customers, setCustomers] = useState([]);
  const [req, setReq] = useState({ customer_id: "", channel: "whatsapp" });
  const [manual, setManual] = useState({ customer_id: "", rating: 5, comment: "" });
  const [reqOpen, setReqOpen] = useState(false);
  const [manOpen, setManOpen] = useState(false);

  const load = () => api.get("/feedback").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => {
    load();
    api.get("/customers").then(r => setCustomers(r.data)).catch(() => {});
  }, []);

  const sendReq = async () => {
    if (!req.customer_id) { toast.error("Pick a customer"); return; }
    try {
      const { data } = await api.post("/feedback/request", req);
      toast.success(`Feedback request queued (${req.channel}) — token ${data.token.slice(0, 8)}`);
      setReqOpen(false); setReq({ customer_id: "", channel: "whatsapp" }); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const saveManual = async () => {
    if (!manual.customer_id) { toast.error("Pick a customer"); return; }
    try {
      await api.post(`/feedback/manual?customer_id=${manual.customer_id}&rating=${manual.rating}${manual.comment ? "&comment=" + encodeURIComponent(manual.comment) : ""}`);
      toast.success("Feedback saved");
      setManOpen(false); setManual({ customer_id: "", rating: 5, comment: "" }); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => {
    if (!confirm("Delete this feedback?")) return;
    try { await api.delete(`/feedback/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const submitted = list.filter(f => f.status === "submitted");
  const avg = submitted.length ? submitted.reduce((s, f) => s + (f.rating || 0), 0) / submitted.length : 0;

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Star className="w-3 h-3"/> CRM · Owner / Manager only</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Feedback</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="border border-border bg-card rounded-sm px-4 py-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Avg Rating</div>
            <div className="font-display font-black text-2xl text-primary flex items-baseline gap-1" data-testid="avg-rating">
              {avg.toFixed(1)}<Star className="w-4 h-4 fill-primary text-primary"/>
              <span className="text-xs text-muted-foreground ml-1">/ {submitted.length}</span>
            </div>
          </div>
          <Dialog open={reqOpen} onOpenChange={setReqOpen}>
            <DialogTrigger asChild>
              <Button data-testid="req-fb-btn" variant="outline" className="rounded-sm text-xs uppercase tracking-wider"><Send className="w-3 h-3 mr-1"/> Request Feedback</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-md">
              <DialogHeader><DialogTitle className="font-display uppercase">Request Feedback</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
                  <Select value={req.customer_id} onValueChange={(v) => setReq(x => ({ ...x, customer_id: v }))}>
                    <SelectTrigger data-testid="req-cust" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Pick customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Channel</Label>
                  <Select value={req.channel} onValueChange={(v) => setReq(x => ({ ...x, channel: v }))}>
                    <SelectTrigger className="mt-1 rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-muted-foreground border-t border-border pt-2">
                  Delivery is queued — actual WhatsApp/SMS send needs Twilio configured. Share the generated feedback link with the customer for now.
                </div>
                <Button data-testid="send-req-btn" onClick={sendReq} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Queue Request</Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={manOpen} onOpenChange={setManOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-fb-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs"><Plus className="w-4 h-4 mr-1"/> Add Manually</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card border-border max-w-md">
              <DialogHeader><DialogTitle className="font-display uppercase">Add Feedback</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
                  <Select value={manual.customer_id} onValueChange={(v) => setManual(x => ({ ...x, customer_id: v }))}>
                    <SelectTrigger data-testid="man-cust" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Pick customer"/></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Rating</Label>
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} data-testid={`star-${n}`} onClick={() => setManual(x => ({ ...x, rating: n }))} className="p-1">
                        <Star className={`w-6 h-6 ${n <= manual.rating ? "fill-primary text-primary" : "text-muted-foreground"}`}/>
                      </button>
                    ))}
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Comment</Label><Textarea data-testid="man-comment" value={manual.comment} onChange={(e) => setManual(x => ({ ...x, comment: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={3}/></div>
                <Button data-testid="save-man-btn" onClick={saveManual} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Save</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Customer</th><th className="text-left p-3">Rating</th>
              <th className="text-left p-3">Comment</th><th className="text-left p-3">Channel</th>
              <th className="text-left p-3">Status</th><th className="text-left p-3">Date</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No feedback yet</td></tr>}
            {pg.pageItems.map(f => (
              <tr key={f.id} data-testid={`fb-row-${f.id}`} className="border-t border-border">
                <td className="p-3">
                  {f.customer_name}
                  <div className="text-[10px] font-mono text-muted-foreground">{f.customer_phone}</div>
                </td>
                <td className="p-3">
                  {f.rating ? (
                    <div className="flex gap-0.5">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= f.rating ? "fill-primary text-primary" : "text-muted-foreground"}`}/>)}</div>
                  ) : <span className="text-muted-foreground text-xs italic">Awaiting</span>}
                </td>
                <td className="p-3 text-xs italic text-muted-foreground max-w-sm truncate">{f.comment || "-"}</td>
                <td className="p-3 text-xs uppercase flex items-center gap-1"><MessageCircle className="w-3 h-3"/>{f.channel}</td>
                <td className="p-3 text-xs"><span className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${f.status === "submitted" ? "border-green-500/40 bg-green-500/10 text-green-500" : "border-yellow-500/40 bg-yellow-500/10 text-yellow-500"}`}>{f.status}</span></td>
                <td className="p-3 font-mono text-xs">{(f.submitted_at || f.requested_at || "").slice(0, 10)}</td>
                <td className="p-3 text-right">
                  <Button data-testid={`del-fb-${f.id}`} size="sm" variant="ghost" onClick={() => del(f.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );
}
